import React, { useState, useEffect } from "react"
import { Col, Row, Spinner, Table } from "reactstrap"
import api from "API/Api"
import { usePermissions } from "API/PermissionContext"

const RouteCollectionReport = () => {
  const { canExport, MODULE } = usePermissions()
  const MID = MODULE.REPORTS

  const [loading, setLoading] = useState(true)
  const [data, setData] = useState([])
  const [totals, setTotals] = useState(null)

  const [from, setFrom] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10))
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10))
  const [source, setSource] = useState("vlcc")
  const [sortField, setSortField] = useState("totalQty")
  const [sortAsc, setSortAsc] = useState(false)

  useEffect(() => { loadData() }, [from, to, source])

  const loadData = async () => {
    setLoading(true)
    try {
      const resp = await api.get("reports/route-collection", { params: { from, to, source } })
      if (resp.data.success) {
        setData(resp.data.data || [])
        setTotals(resp.data.totals || null)
      }
    } catch (e) { console.error(e) }
    setLoading(false)
  }

  const exportCSV = () => {
    window.open(`${api.defaults.baseURL}/reports/route-collection?from=${from}&to=${to}&source=${source}&export=csv`, "_blank")
  }

  const handleSort = (field) => {
    if (sortField === field) setSortAsc(!sortAsc)
    else { setSortField(field); setSortAsc(false) }
  }

  const sorted = [...data].sort((a, b) => {
    const av = a[sortField] ?? 0, bv = b[sortField] ?? 0
    return sortAsc ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1)
  })

  const maxQty = Math.max(...data.map(d => d.totalQty), 1)

  const thSort = (label, field) => (
    <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleSort(field)}>
      {label} {sortField === field ? (sortAsc ? "↑" : "↓") : ""}
    </th>
  )

  const cardStyle = (color) => ({
    background: `linear-gradient(135deg, ${color}12, ${color}06)`, borderLeft: `4px solid ${color}`,
    borderRadius: "12px", padding: "18px 20px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
  })

  return (
    <div className="dashBody">
      <div className="font-size-18 mb-0 dashHeader">
        <Row>
          <Col md={6}><h4><i className="fas fa-route me-2" style={{ color: "#6366f1" }}></i>Route Collection Report</h4></Col>
          <Col md={6} style={{ textAlign: "right" }}>
            {canExport(MID) && <button className="btn btn-success btn-sm" onClick={exportCSV}><i className="fas fa-file-csv me-1"></i>Export CSV</button>}
          </Col>
        </Row>
      </div>
      <div className="dashContent">

        {/* Filters */}
        <Row className="mb-3" style={{ background: "#f8fafc", padding: "12px", borderRadius: "10px" }}>
          <Col md={2}><label style={{ fontSize: "11px", fontWeight: "600", color: "#64748b" }}>From</label><input type="date" className="form-control form-control-sm" value={from} onChange={e => setFrom(e.target.value)} /></Col>
          <Col md={2}><label style={{ fontSize: "11px", fontWeight: "600", color: "#64748b" }}>To</label><input type="date" className="form-control form-control-sm" value={to} onChange={e => setTo(e.target.value)} /></Col>
          <Col md={3}>
            <label style={{ fontSize: "11px", fontWeight: "600", color: "#64748b" }}>Source</label>
            <div style={{ display: "flex", gap: "4px" }}>
              <button className={`btn btn-sm ${source === "vlcc" ? "btn-primary" : "btn-outline-primary"}`} onClick={() => setSource("vlcc")}>🥛 VLCC</button>
              <button className={`btn btn-sm ${source === "cc" ? "btn-info" : "btn-outline-info"}`} onClick={() => setSource("cc")}>🏭 CC</button>
            </div>
          </Col>
        </Row>

        {loading ? <div style={{ textAlign: "center", padding: "60px" }}><Spinner color="primary" /></div> : (
          <>
            {/* Stats */}
            {totals && (
              <Row className="mb-4">
                {[
                  { label: "Total Routes", value: totals.routeCount, color: "#6366f1" },
                  { label: "Total Entries", value: totals.totalEntries?.toLocaleString("en-IN"), color: "#0ea5e9" },
                  { label: "Total Suppliers", value: totals.totalSuppliers, color: "#8b5cf6" },
                  { label: "Total Qty (Ltrs)", value: Number(totals.totalQty).toLocaleString("en-IN", { minimumFractionDigits: 2 }), color: "#22c55e" },
                  { label: "Total Amount", value: "₹" + Number(totals.totalAmount).toLocaleString("en-IN", { minimumFractionDigits: 2 }), color: "#f59e0b" },
                ].map((c, i) => (
                  <Col key={i} className="mb-3">
                    <div style={cardStyle(c.color)}>
                      <div style={{ fontSize: "10px", color: "#6c757d", textTransform: "uppercase", letterSpacing: "1px", fontWeight: "600" }}>{c.label}</div>
                      <div style={{ fontSize: "20px", fontWeight: "700", color: "#1e293b", marginTop: "4px" }}>{c.value}</div>
                    </div>
                  </Col>
                ))}
              </Row>
            )}

            {/* Visual Bar + Table */}
            {sorted.length === 0 ? (
              <div style={{ textAlign: "center", padding: "60px", background: "#fff", borderRadius: "12px" }}>
                <i className="fas fa-inbox" style={{ fontSize: "48px", color: "#cbd5e1", marginBottom: "12px" }}></i>
                <p style={{ color: "#94a3b8" }}>No collection data found</p>
              </div>
            ) : (
              <div style={{ background: "#fff", borderRadius: "12px", overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
                <div className="table-responsive">
                  <Table className="table mb-0" size="sm">
                    <thead>
                      <tr style={{ background: "#f8fafc", fontSize: "11px", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                        <th>#</th>
                        <th>Route</th>
                        {thSort("Suppliers", "supplierCount")}
                        {thSort("Entries", "entries")}
                        {thSort("Qty (Ltrs)", "totalQty")}
                        <th style={{ width: "200px" }}>Distribution</th>
                        {thSort("Avg FAT", "avgFat")}
                        {thSort("Avg SNF", "avgSnf")}
                        {thSort("Amount", "totalAmount")}
                      </tr>
                    </thead>
                    <tbody>
                      {sorted.map((d, i) => (
                        <tr key={d.route_id || i} style={{ fontSize: "13px", borderBottom: "1px solid #f1f5f9" }}>
                          <td style={{ color: "#94a3b8" }}>{i + 1}</td>
                          <td style={{ fontWeight: "600", color: "#1e293b" }}>{d.routeName}</td>
                          <td><span className="badge bg-info" style={{ fontSize: "11px" }}>{d.supplierCount}</span></td>
                          <td>{d.entries}</td>
                          <td style={{ fontWeight: "600", color: "#0284c7" }}>{Number(d.totalQty).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                          <td>
                            <div style={{ background: "#f1f5f9", borderRadius: "4px", height: "16px", overflow: "hidden" }}>
                              <div style={{ background: "linear-gradient(90deg, #6366f1, #818cf8)", width: `${Math.max((d.totalQty / maxQty) * 100, 2)}%`, height: "100%", borderRadius: "4px", transition: "width 0.5s" }}></div>
                            </div>
                          </td>
                          <td><span style={{ background: "#f0fdf4", color: "#166534", padding: "2px 8px", borderRadius: "8px", fontSize: "12px" }}>{Number(d.avgFat).toFixed(2)}</span></td>
                          <td><span style={{ background: "#fef3c7", color: "#92400e", padding: "2px 8px", borderRadius: "8px", fontSize: "12px" }}>{Number(d.avgSnf).toFixed(2)}</span></td>
                          <td style={{ fontWeight: "700", color: "#059669" }}>₹{Number(d.totalAmount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default RouteCollectionReport
